import { useMemo } from 'react'
import { useQuery, useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { UserGroup03Icon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Table from '@/components/ui/Table'
import Response from './Response'
import OrderStateWithInfo from './OrderStateWithInfo'
import { amount } from '../utils/helpers'

const stateMapping = {
  [GraphQL.OrderState.Approved]: 1,
  [GraphQL.OrderState.Pending]: 2,
  [GraphQL.OrderState.Cancelled]: 3,
  [GraphQL.OrderState.Rejected]: 4
}

function ParticipantRow({ order }: { order: GraphQL.OrderFragmentFragment }) {
  const { data } = useQuery(AdminAPI.stay.GET, {
    variables: { id: order.stayId, withSpace: true }
  })

  const stay = useMemo(() => {
    return getFragment(SharedAPI.stay.STAY_FRAGMENT, data?.getStay)
  }, [data])

  const space = useMemo(() => {
    return getFragment(SharedAPI.space.SPACE_FRAGMENT, stay?.space)
  }, [stay])

  return (
    <Table.Row>
      <Table.Cell>{space?.identifier}</Table.Cell>
      <Table.Cell>{amount(order)}</Table.Cell>
      <Table.Cell>{order.state}</Table.Cell>
      <Table.Cell>{order.guestMessage}</Table.Cell>
      <Table.Cell>
        {order.state === GraphQL.OrderState.Pending && (
          <Response order={order} />
        )}
        {order.state !== GraphQL.OrderState.Pending && (
          <OrderStateWithInfo order={order} />
        )}
      </Table.Cell>
    </Table.Row>
  )
}

export default function EventParticipants({ id }: { id: number }) {
  const { t } = useLingui()

  const { data: eventOccurenceData } = useSuspenseQuery(
    AdminAPI.eventOccurence.GET,
    {
      variables: { id }
    }
  )

  const eventOccurence = useMemo(
    () =>
      getFragment(
        SharedAPI.eventOccurence.EVENT_OCCURENCE_FRAGMENT,
        eventOccurenceData.getEventOccurence
      ),
    [eventOccurenceData]
  )

  const { data: orderData } = useSuspenseQuery(AdminAPI.order.GET_BY_EVENT, {
    variables: {
      eventOccurenceId: eventOccurence.id
    }
  })

  const orders = useMemo(() => {
    return orderData.ordersByEvent
      .map(function (order) {
        return getFragment(SharedAPI.order.ORDER_FRAGMENT, order)
      })
      .toSorted((oA, oB) => stateMapping[oA.state] - stateMapping[oB.state])
  }, [orderData])

  const seatsTaken = eventOccurence.seatsTaken
  const maxSeats = eventOccurence.maxSeats ? `/${eventOccurence.maxSeats}` : ''

  return (
    <Table.Root>
      <Table.Head>
        <Table.Row>
          <Table.Heading>
            <Trans>Room</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>
              Number ({seatsTaken}
              {maxSeats})
            </Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Status</Trans>
          </Table.Heading>
          <Table.Heading>
            <Trans>Guest Message</Trans>
          </Table.Heading>
          <Table.Heading />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {orders &&
          orders.map(order => <ParticipantRow key={order.id} order={order} />)}
        {orders.length === 0 && (
          <Table.Empty Icon={UserGroup03Icon} text={t`No Participants found`} />
        )}
      </Table.Body>
    </Table.Root>
  )
}
